import type { FC, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { SectionCard } from './SectionCard';
import { SectionLabel } from './typography';
import styles from './EditableSectionCard.module.css';

interface EditableSectionCardProps {
  label: string;
  summary: ReactNode;
  children: ReactNode;
  focusTargetRef?: RefObject<HTMLElement | null>;
  invalid?: boolean;
  rightSlot?: ReactNode;
  triggerTestId?: string;
  onCollapse?: () => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const EditableSectionCard: FC<EditableSectionCardProps> = ({
  label,
  summary,
  children,
  focusTargetRef,
  invalid = false,
  rightSlot,
  triggerTestId,
  onCollapse,
  expanded,
  onExpandedChange,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = expanded ?? internalIsEditing;
  const [editorMounted, setEditorMounted] = useState(isEditing);
  const isControlled = expanded !== undefined;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isEditingRef = useRef(isEditing);
  const editorId = useId();

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  const updateEditing = useCallback(
    (next: boolean): void => {
      isEditingRef.current = next;
      if (!isControlled) {
        setInternalIsEditing(next);
      }
      onExpandedChange?.(next);
    },
    [isControlled, onExpandedChange]
  );

  const collapse = useCallback(
    (restoreFocus = false): void => {
      if (!isEditingRef.current) return;
      updateEditing(false);
      onCollapse?.();
      if (restoreFocus) {
        queueMicrotask(() => triggerRef.current?.focus());
      }
    },
    [onCollapse, updateEditing]
  );

  useEffect(() => {
    if (isEditing) {
      setEditorMounted(true);
      focusTargetRef?.current?.focus({ preventScroll: true });
      return;
    }

    // Keep contents during collapse; transitionend normally releases them.
    // Keep this 240 ms fallback in sync with the CSS --duration-base token.
    const timeout = window.setTimeout(
      () => setEditorMounted(false),
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 240
    );
    return () => window.clearTimeout(timeout);
  }, [focusTargetRef, isEditing]);

  useEffect(() => {
    if (!isEditing) return;

    const isOutside = (target: EventTarget | null): boolean =>
      target instanceof Node && !rootRef.current?.contains(target);

    const handleClick = (event: MouseEvent): void => {
      if (isOutside(event.target)) collapse();
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [collapse, isEditing]);

  return (
    <div
      ref={rootRef}
      data-expanded={isEditing}
      onKeyDownCapture={(event) => {
        if (isEditing && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          collapse(true);
        }
      }}
    >
      <SectionCard tight className={styles.card}>
        <div className={styles.header}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.trigger}
            aria-label={isEditing ? `Finish editing ${label}` : `Edit ${label}`}
            aria-expanded={isEditing}
            aria-controls={editorId}
            data-testid={triggerTestId}
            onClick={() => {
              if (isEditing) {
                collapse();
              } else {
                updateEditing(true);
              }
            }}
          >
            <SectionLabel>{label}</SectionLabel>
            <span className={styles.summaryShell} aria-hidden={isEditing}>
              <span className={invalid ? styles.summaryInvalid : styles.summary}>{summary}</span>
            </span>
            <ChevronDown size={18} className={styles.indicator} aria-hidden="true" />
          </button>
          {rightSlot !== undefined && rightSlot !== null && (
            <div className={styles.rightSlotShell} inert={!isEditing} aria-hidden={!isEditing}>
              <div className={styles.rightSlot}>{rightSlot}</div>
            </div>
          )}
        </div>

        <div
          id={editorId}
          className={styles.editorShell}
          inert={!isEditing}
          aria-hidden={!isEditing}
          onTransitionEnd={(event) => {
            if (
              event.target === event.currentTarget &&
              event.propertyName === 'grid-template-rows' &&
              !isEditing
            ) {
              setEditorMounted(false);
            }
          }}
        >
          <div className={styles.editorClip}>
            <div className={styles.editor}>{(isEditing || editorMounted) && children}</div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};
